import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { IRootState } from "../../../../../reducer/CombineReducer";
import { OutputMappingListCard } from "../OutputMappingListCard/OutputMappingListCard.logic";
import { OutputMappingItem, OutputMappingChild } from "./Types";
import "./Style.scss";

interface childMap {
  /**
   * Child name
   */
  childName: string;
  /**
   * Config name
   */
  configName: string;
  /**
   * Config map id
   */
  configMapId: string;
  /**
   * Config tag
   */
  tag: string;
}

/**
 * Responsible to list view
 * @returns list view
 */
export const OutputMappingListView: React.FC = () => {
  const outputConfig = useSelector((state: IRootState) => state.outputConfig);
  const { outputMappingList, mappingFilters, searchByTagName } = outputConfig;
  const { searchTerm } = mappingFilters;
  const [filteredList, setFilteredList] = useState<OutputMappingItem[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setDataList] = useState<childMap[]>([]);
  const [outputMappingFlatList, setOutputMappingFlatList] = useState<OutputMappingItem[]>([]);
  const data = [] as childMap[];
  const outputMappingDataFlatList = [] as OutputMappingItem[];

  /**
   * Recursion through children maps
   * @param configName
   * @param configMapId
   * @param child
   */
  const normalizeChild = (
    tag: string,
    configName: string,
    configMapId: string,
    child: OutputMappingChild | OutputMappingItem): void => {
    const { childrenOutputElements } = child;
    if (childrenOutputElements && childrenOutputElements.length > 0) {
      childrenOutputElements.forEach((item: OutputMappingChild) => {
        data.push(
          {
            tag: item.tag.toLowerCase(),
            childName: item?.configName?.toLowerCase(),
            configName: configName,
            configMapId: configMapId
          }
        );
        outputMappingDataFlatList.push(item);
        normalizeChild(tag, configName, configMapId, item);
      });
    }
  };

  /**
   * Loop through config map list
   * @param list OutputMappingItem
   */
  const listLoop = (list: OutputMappingItem[]): void => {
    if (list && list.length > 0) {
      list.forEach((item: OutputMappingItem) => {
        if (item && item.childrenOutputElements && item.childrenOutputElements.length > 0) {
          normalizeChild(item?.tag.toLowerCase(), item.configName, item.configMapId, item);
        } else {
          outputMappingDataFlatList.push(item);
        }
      });
    }
  };

  /**
   * Function that filteres the mapped config list based on the search tag
   * @param outputMappingList - available mapping list for the current config id
   * @param searchTerm - search tag
   * @returns - mapping list, which includes the search tag.
   */
  const filterNestedObjectsBySearchTag = (
    outputMappingList: OutputMappingItem[],
    searchTerm: string
  ): OutputMappingItem[] => {
    return outputMappingList.reduce((filteredArr: OutputMappingItem[], obj: OutputMappingItem) => {
      // Check if the search term is present in any of the object's properties
      const hasSearchTerm = Object.values(obj).some(value => {
        if (typeof value === "string") {
          return value.includes(searchTerm);
        }
        return false;
      });

      if (hasSearchTerm) {
        // Include the object in the filtered array
        filteredArr.push(obj);
      }

      // Recursively process nested arrays (if present)
      if (obj.childrenOutputElements && Array.isArray(obj.childrenOutputElements)) {
        const filteredChildren = filterNestedObjectsBySearchTag(obj.childrenOutputElements, searchTerm);
        if (filteredChildren.length > 0) {
          // Include the object with filtered children in the filtered array
          filteredArr.push({ ...obj, childrenOutputElements: filteredChildren } as OutputMappingItem);
        }
      }

      return filteredArr;
    }, []);
  };

  /**
    * Update filtered list when search band changes
    */
  useEffect(() => {
    if (searchByTagName !== "" && outputMappingFlatList.length > 0) {
      const filteredConfigList = filterNestedObjectsBySearchTag(outputMappingList, searchByTagName);
      setFilteredList(filteredConfigList);
    }
    else {
      setFilteredList(outputMappingList);
    }
  }, [searchByTagName]);

  /**
   * Update filtered list when original list changes
   */
  useEffect(() => {
    listLoop(outputMappingList);
    setDataList(data);
    setOutputMappingFlatList(outputMappingDataFlatList);
    setFilteredList(outputMappingList);
  }, [outputMappingList]);

  /**
   * Update filtered list when search term changes
   */
  useEffect(() => {
    const term = (searchTerm && searchTerm.trim()) || "";
    if (term && term.length > 0) {
      const filtered = outputMappingList.filter((item: OutputMappingItem) => {
        return item?.configName?.toLowerCase().includes(searchTerm?.toLowerCase());
      });
      setFilteredList(filtered);
    }
    else if (term.length === 0) {
      setFilteredList(outputMappingList);
    }
  }, [searchTerm]);

  return (
    <div
      className="output-config__mapping-list-view-container "
      id="ebt-outputConfig-config-container"
    >
      {
        filteredList && filteredList.length > 0 ?
          (filteredList.map((item: OutputMappingItem, idx: number) =>
            <OutputMappingListCard
              key={idx}
              mappedConfig={item}
              currentMapIndex={idx}
            />
          ))
          : null
      }
    </div>
  );
};
